import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import BarChart from '../components/BarChart';
import { OrderStatusBadge, OrderDetail } from '../components/OrderCard';
import { apiFetch } from '../utils/apiClient';
import { formatPrice, formatCompactPrice, formatShortDate, formatDate } from '../utils/format';
import { useVendor } from '../contexts/VendorContext';

interface PeriodStats {
  revenue: number;
  units: number;
  orders: number;
}

interface DashboardData {
  period_days: number;
  vendor: { id: number; name: string } | null;
  sales: { current: PeriodStats; previous: PeriodStats };
  daily: { date: string; revenue: number; units: number }[];
  top_titles: { title: string; units: number; revenue: number }[];
  stock: {
    items: number;
    active_items: number;
    units_available: number;
    out_of_stock: number;
    low_stock: number;
    value_at_selling: number;
    sync_failed: number;
    sync_pending: number;
    unsynced: number;
  };
  orders: { awaiting_confirmation: number; recent: OrderDetail[] };
}

interface VendorOption {
  id: number;
  name: string;
}

const Delta: React.FC<{ current: number; previous: number; days: number }> = ({ current, previous, days }) => {
  const { t } = useTranslation();
  if (!previous && !current) return null;
  const pct = previous ? ((current - previous) / previous) * 100 : 100;
  const up = pct >= 0;
  return (
    <p className={`mt-1 text-xs flex items-center gap-1 ${up ? 'text-green-700' : 'text-red-700'}`}>
      <span aria-hidden>{up ? '▲' : '▼'}</span>
      <span>{Math.abs(pct).toFixed(0)}%</span>
      <span className="text-gray-500">{t('dashboard.vsPrevious', { days })}</span>
    </p>
  );
};

const Tile: React.FC<{ label: string; value: string; children?: React.ReactNode; tone?: 'default' | 'warn' | 'bad'; to?: string }> = ({ label, value, children, tone = 'default', to }) => {
  const border = tone === 'bad' ? 'border-red-200' : tone === 'warn' ? 'border-orange-200' : 'border-gray-200';
  const body = (
    <div className={`bg-white rounded-lg border ${border} p-4 h-full`}>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="mt-1 text-xl sm:text-2xl font-bold text-gray-900 font-display break-words">{value}</p>
      {children}
    </div>
  );
  return to ? <Link to={to} className="block active:opacity-80">{body}</Link> : body;
};

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { user, vendor, isAdmin } = useVendor();
  const [period, setPeriod] = useState(30);
  const [vendorFilter, setVendorFilter] = useState('');
  const [vendors, setVendors] = useState<VendorOption[]>([]);
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period: String(period) });
      if (isAdmin && vendorFilter) params.set('vendor', vendorFilter);
      const r = await apiFetch(`/api/dashboard/?${params.toString()}`);
      if (!r.ok) throw new Error(t('common.genericError'));
      setData(await r.json());
      setError('');
    } catch (err: any) {
      setError(err.message || t('common.genericError'));
    } finally {
      setLoading(false);
    }
  }, [period, vendorFilter, isAdmin, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!isAdmin) return;
    apiFetch('/api/vendors/?page_size=200')
      .then((r) => (r.ok ? r.json() : { results: [] }))
      .then((d) => setVendors((d.results || d || []).map((v: any) => ({ id: v.id, name: v.name }))))
      .catch(() => undefined);
  }, [isAdmin]);

  const name = user?.first_name || vendor?.name || user?.username || '';

  return (
    <Layout>
      <div className="w-full">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('dashboard.title')}</h1>
              <p className="text-sm text-gray-500">{t('dashboard.greeting', { name })}</p>
            </div>
            <Link
              to="/scanner"
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 min-h-[44px] text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 whitespace-nowrap"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 7V5a2 2 0 012-2h2M17 3h2a2 2 0 012 2v2M21 17v2a2 2 0 01-2 2h-2M7 21H5a2 2 0 01-2-2v-2M7 8v8M11 8v8M15 8v8" />
              </svg>
              {t('dashboard.scanButton')}
            </Link>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {isAdmin && (
              <select
                value={vendorFilter}
                onChange={(e) => setVendorFilter(e.target.value)}
                className="rounded-md border border-gray-300 bg-white text-sm px-3 py-2 min-h-[40px]"
                aria-label={t('dashboard.vendorFilter')}
              >
                <option value="">{t('dashboard.allVendors')}</option>
                {vendors.map((v) => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
            )}
            <div className="inline-flex rounded-md border border-gray-300 bg-white overflow-hidden">
              {[7, 30, 90].map((d) => (
                <button
                  key={d}
                  onClick={() => setPeriod(d)}
                  className={`px-3 py-2 text-sm min-h-[40px] ${period === d ? 'bg-blue-600 text-white' : 'text-gray-700'}`}
                >
                  {t(`dashboard.period${d}`)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {error && <div className="rounded-md bg-red-50 p-4 mb-4 text-sm text-red-800">{error}</div>}

        {loading || !data ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {data.orders.awaiting_confirmation > 0 && (
              <Link to="/orders" className="block rounded-lg border-2 border-orange-300 bg-orange-50 p-4 flex items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-orange-900">{t('dashboard.awaitingConfirmation')}</p>
                  <p className="text-sm text-orange-800">{t('dashboard.awaitingBody', { count: data.orders.awaiting_confirmation })}</p>
                </div>
                <span className="text-sm font-medium text-orange-900 whitespace-nowrap">{t('dashboard.viewOrders')} →</span>
              </Link>
            )}

            {/* Sales tiles */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Tile label={t('dashboard.revenue')} value={formatPrice(data.sales.current.revenue)}>
                <Delta current={data.sales.current.revenue} previous={data.sales.previous.revenue} days={data.period_days} />
              </Tile>
              <Tile label={t('dashboard.unitsSold')} value={String(data.sales.current.units)}>
                <Delta current={data.sales.current.units} previous={data.sales.previous.units} days={data.period_days} />
              </Tile>
              <Tile label={t('dashboard.orders')} value={String(data.sales.current.orders)} to="/orders">
                <Delta current={data.sales.current.orders} previous={data.sales.previous.orders} days={data.period_days} />
              </Tile>
            </div>

            {/* Daily chart + top titles */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 p-4">
                <h2 className="text-sm font-semibold text-gray-900 mb-2">{t('dashboard.dailySales')}</h2>
                {data.sales.current.revenue === 0 ? (
                  <p className="text-sm text-gray-500 py-10 text-center">{t('dashboard.noSales')}</p>
                ) : (
                  <BarChart
                    ariaLabel={t('dashboard.dailySales')}
                    data={data.daily.map((d) => ({
                      label: formatShortDate(d.date),
                      value: d.revenue,
                      secondary: t('dashboard.units', { count: d.units }),
                    }))}
                    formatValue={(v) => formatCompactPrice(v)}
                  />
                )}
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h2 className="text-sm font-semibold text-gray-900 mb-2">{t('dashboard.topTitles')}</h2>
                {data.top_titles.length === 0 ? (
                  <p className="text-sm text-gray-500">{t('dashboard.noSales')}</p>
                ) : (
                  <ol className="space-y-2">
                    {data.top_titles.map((row, i) => {
                      const max = data.top_titles[0].units || 1;
                      return (
                        <li key={row.title + i} className="text-sm">
                          <div className="flex justify-between gap-2">
                            <span className="truncate text-gray-900">{i + 1}. {row.title}</span>
                            <span className="text-gray-600 whitespace-nowrap">{t('dashboard.units', { count: row.units })}</span>
                          </div>
                          <div className="h-1.5 bg-gray-100 rounded-full mt-1">
                            <div className="h-1.5 rounded-full bg-blue-600" style={{ width: `${(row.units / max) * 100}%` }} />
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </div>
            </div>

            {/* Stock tiles */}
            <h2 className="text-sm font-semibold text-gray-900 pt-2">{t('dashboard.stock')}</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Tile label={t('dashboard.unitsAvailable')} value={String(data.stock.units_available)} to="/inventory" />
              <Tile label={t('dashboard.stockValueSelling')} value={formatPrice(data.stock.value_at_selling)} />
              <Tile label={t('dashboard.outOfStock')} value={String(data.stock.out_of_stock)} tone={data.stock.out_of_stock ? 'warn' : 'default'} to="/inventory?stock=out">
                <p className="mt-1 text-xs text-gray-500">{t('dashboard.lowStock')}: {data.stock.low_stock}</p>
              </Tile>
              <Tile label={t('dashboard.syncFailed')} value={String(data.stock.sync_failed)} tone={data.stock.sync_failed ? 'bad' : 'default'}>
                <p className="mt-1 text-xs text-gray-500">{t('dashboard.unsynced')}: {data.stock.unsynced}</p>
              </Tile>
            </div>

            {/* Recent orders */}
            {data.orders.recent.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200">
                <div className="px-4 py-3 border-b border-gray-100 flex justify-between items-center">
                  <h2 className="text-sm font-semibold text-gray-900">{t('dashboard.recentOrders')}</h2>
                  <Link to="/orders" className="text-sm text-blue-600">{t('dashboard.viewOrders')}</Link>
                </div>
                <ul className="divide-y divide-gray-100">
                  {data.orders.recent.map((o) => (
                    <li key={o.id} className="px-4 py-3 flex flex-wrap items-center justify-between gap-2 text-sm">
                      <div>
                        <span className="font-medium text-gray-900">{o.order_number || `#${o.id}`}</span>
                        <span className="text-gray-500"> · {o.customer_name || '—'}{o.shipping_city ? `, ${o.shipping_city}` : ''}</span>
                        <p className="text-xs text-gray-500">{formatDate(o.received_at)}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-medium">{formatPrice(o.subtotal)}</span>
                        <OrderStatusBadge order={o} />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;
